import { convertBytesToSize } from '@utils/fileSizeFormatter';

import { IAssetFile } from '@interfaces/assets/asset';

import { fileExtensions } from '@constants/common';

export const isPrimitive = (value: any) => (typeof value !== 'object' && !Array.isArray(value)) || value === null;

const getCommonKeys = (previous: Record<string, any>, current: Record<string, any>) => {
	const previousKeys = previous ? Object.keys(previous) : [];
	const currentKeys = current ? Object.keys(current) : [];
	const keys = [...previousKeys, ...currentKeys];

	return [...Array.from(new Set(keys))];
};

const compareArray = ({ current, previous }: { current: Record<string, any>; previous: Record<string, any> }) => {
	if (!previous && !current) return {};

	const toObj = (arr: Record<string, any>) =>
		arr.reduce((acc: Record<string, any>, next: Record<string, any>) => ({ ...acc, [next.id]: next }), {});

	const currentItem = current ? toObj(current) : {};
	const previousItem = previous ? toObj(previous) : {};

	const uniqueKeys = getCommonKeys(currentItem, previousItem) as any[];

	const result = uniqueKeys.reduce<Record<string, any>>((acc: Record<string, any>, next: string) => {
		const value = {
			current: next in currentItem ? currentItem[next] : null,
			previous: next in previousItem ? previousItem[next] : null,
			isChanged: !(next in currentItem && next in previousItem),
		};

		return { ...acc, [next]: value };
	}, {});

	return result;
};

const addTestId = ({
	field,
	fieldName,
	setId,
}: {
	field: Record<string, any> | null;
	fieldName?: string;
	setId?: boolean;
}) => (field && fieldName && setId ? { ...field, testId: fieldName } : field);

export const getField = (obj: Record<string, any>, fieldName: string, setId = true): Record<string, any> | null => {
	if (!fieldName) return null;

	type TKeys = keyof typeof obj;

	const fields = fieldName as TKeys | TKeys[];

	if (!Array.isArray(fields)) {
		const field = obj?.[fieldName];

		return addTestId({ field, fieldName, setId });
	}

	let result: Record<string, any> | null = obj;
	let testIdName = '';

	for (const key of fields) {
		if (isPrimitive(result) || !result?.[key]) return null;

		result = result?.[key];
		testIdName += key;
	}

	return addTestId({ field: result, fieldName: testIdName, setId });
};

const addChangedField = ({
	acc,
	current,
	previous,
	next,
}: {
	acc: Record<string, any>;
	current: Record<string, any>;
	previous: Record<string, any>;
	next: string;
}) => {
	acc[next] = {
		previous: previous,
		current: current,
		isChanged: previous !== current,
	};

	return acc;
};

export const getChangedFieldsRecursive = ({
	previous,
	current,
}: {
	previous: Record<string, any> | null;
	current: Record<string, any> | null;
}) => {
	if (!previous && !current) return {};

	if (!previous) previous = {};
	if (!current) current = {};

	const uniqueKeys = getCommonKeys(previous, current);

	const changedFields = uniqueKeys.reduce<Record<string, any>>((acc: Record<string, any>, next: string) => {
		const currentValue = current?.[next];
		const previousValue = previous?.[next];

		if (isPrimitive(previousValue || currentValue)) {
			return addChangedField({
				acc,
				previous: previousValue,
				current: currentValue,
				next,
			});
		}

		if (Array.isArray(previousValue || currentValue)) {
			acc[next] = compareArray({ current: currentValue, previous: previousValue });

			return acc;
		}

		if (!(Array.isArray(previousValue) || Array.isArray(previousValue))) {
			acc[next] = getChangedFieldsRecursive({
				previous: previousValue,
				current: currentValue,
			});

			return acc;
		}

		return acc;
	}, {});

	return changedFields;
};

export const getExt = (file: IAssetFile | null) => {
	if (!file) return null;

	const extension = file?.type?.title || fileExtensions[file?.mimeType] || '';
	const convertedSize = convertBytesToSize(file.size);

	return {
		extension,
		convertedSize,
	};
};
